import { type FC, useMemo, lazy, Suspense } from 'react';
import { LoaderType, NextgenImageV2 } from '../NextgenImage';
import { Breakpoints, useBreakpoint } from '../Breakpoint/BreakpointProvider';
import { Theme } from '../types/enums';
import TextOnly, { type TextOnlyTheme } from '../TextOnly/TextOnly';
import SkeletonFrame, { SkeletonType } from '../SkeletonFrame/SkeletonFrame';
import { type ButtonTheme, ButtonWidth } from '../Button/Button';
import styles from './ImageAndText.module.scss';
import { type ImageAndTextProptypes } from './types/propsType';
import { ImageAndTextCtaBtnPosition, ImageAndTextDirection } from './types/enums';
const GenericLink = lazy(() => import('../GenericLink/GenericLink'));
const RawButton = lazy(() => import('../Button/RawButton'));

const ImageAndText: FC<ImageAndTextProptypes> = (props: ImageAndTextProptypes) => {
  const {
    id,
    direction,
    theme = Theme.WHITE,
    className,
    containerBorderRadius,
    ctaConfig,
    button,
    imgPerViewport,
    textOnlyConfig,
    loaderType = LoaderType.CONTENTSTACK,
    ...restProps
  } = props;

  const viewport = useBreakpoint() as Breakpoints;
  const {
    src,
    alt,
    borderRadius,
    width = 100,
    height,
    aspectRatio,
  } = useMemo(() => imgPerViewport[viewport || Breakpoints.MD], [viewport, imgPerViewport]);
  const containerClassName = useMemo(
    () =>
      `${styles.container} ${styles[direction ?? ImageAndTextDirection.LEFT_TEXT_RIGHT_IMAGE]} ${
        styles[theme]
      } ${className}`.trim(),
    [direction, theme],
  );

  return (
    <div id={id} className={styles['base-container']} data-testid="base-container-testid">
      <section
        className={containerClassName}
        style={{
          ...(containerBorderRadius ? { borderRadius: containerBorderRadius } : {}),
          ...(textOnlyConfig.backgroundColor ? { backgroundColor: textOnlyConfig.backgroundColor } : {}),
        }}
      >
        <div className={styles['body-text']}>
          <section className={styles['body-text-wrapper']}>
            <TextOnly className={styles['body-text-main']} theme={theme as TextOnlyTheme} {...textOnlyConfig} />
            {button?.title && ctaConfig?.href && (
              <Suspense fallback={<SkeletonFrame widthValue="80" type={SkeletonType.BUTTON} />}>
                <GenericLink
                  href={ctaConfig.href}
                  target={ctaConfig.target}
                  className={`${styles['cta-btn']} ${styles[button?.alignment ?? ImageAndTextCtaBtnPosition.LEFT]}`}
                  data-testid="cta-btn-testid"
                >
                  <RawButton
                    {...button}
                    theme={button.theme || (theme as ButtonTheme)}
                    buttonWidth={ButtonWidth.FIT_CONTENT}
                  />
                </GenericLink>
              </Suspense>
            )}
          </section>
        </div>
        <div className={styles['body-image']}>
          <NextgenImageV2
            {...restProps}
            src={src}
            alt={alt}
            className={styles['img-main']}
            sizes="(max-width: 768px) calc(100vw - 32px), (max-width: 1024px) calc((100vw - 64px) / 2), calc((100vw - 96px) / 2)"
            loading="lazy"
            width={0}
            height={0}
            fill={false}
            decoding="async"
            style={{
              height: height ?? 'auto',
              color: 'transparent',
              width: `${width ?? 100}%`,
              aspectRatio: aspectRatio ?? 'auto',
              ...(borderRadius ? { borderRadius: borderRadius } : {}),
            }}
            loaderType={loaderType}
          />
        </div>
      </section>
    </div>
  );
};
export { ImageAndTextDirection, ImageAndTextCtaBtnPosition };
export type { ImageAndTextProptypes };
ImageAndText.displayName = 'ImageAndText';
export default ImageAndText;
